import {
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { NavItem } from '../layouts/full/vertical/sidebar/nav-item/nav-item';

@Directive({
  standalone: true,
  selector: '[appSidebarNavItemStatus]',
})
export class SidebarNavItemStatusDirective implements OnInit, OnDestroy {
  private static _instances = new Map<NavItem, SidebarNavItemStatusDirective>();
  private static get instances(): SidebarNavItemStatusDirective[] {
    return Array.from(SidebarNavItemStatusDirective._instances.values());
  }

  constructor(private el: ElementRef<HTMLElement>, private router: Router) {}

  private static getInstance(
    item: NavItem
  ): SidebarNavItemStatusDirective | undefined {
    return SidebarNavItemStatusDirective._instances.get(item);
  }

  @Input() item: NavItem;

  itemExpanded: boolean;
  private manuallyCollapsed: boolean = false; // Flag per elementi chiusi manualmente
  @Output('isExpanded') isExpandedEmitter: EventEmitter<boolean> =
    new EventEmitter<boolean>();

  ngOnInit() {
    SidebarNavItemStatusDirective._instances.set(this.item, this);
    Promise.resolve().then(() => {
      this.initialState();
    });
  }

  @HostListener('click')
  onClickNavigation() {
    const hasChildren = !!this.item.children?.length;
    const isDescendant = this.isDescendant();

    // CASO 1: Non è discendente e non ha discendenti
    if (!isDescendant && !hasChildren) {
      this.handleRootWithoutChildren();
    }
    // CASO 2: Non è discendente ma ha discendenti
    else if (!isDescendant && hasChildren) {
      this.handleRootWithChildren();
    }

    // CASO 3: È discendente e ha discendenti
    else if (isDescendant && hasChildren) {
      this.handleChildrenWithChildren();
    }

    // CASO 4: È discendente e non ha discendenti
    else if (isDescendant && !hasChildren) {
      this.handleChildrenWithoutChildren();
    }
  }

  // CASO 1: Reset completo + attivazione elemento cliccato
  private handleRootWithoutChildren() {
    SidebarNavItemStatusDirective.instances.forEach((instance) => {
      instance.collapseItem();
    });
    this.resetAllActiveStates();
    this.setItemToActive();
  }

  // CASO 2: Gestione elementi root con figli
  private handleRootWithChildren() {
    // Chiudi tutti gli altri elementi root (fratelli) e forza il collapse
    SidebarNavItemStatusDirective.instances.forEach((instance) => {
      if (instance !== this && this.findParent(instance.item) === null) {
        instance.forceCollapseItem();
      }
    });

    // Toggle espansione
    if (this.itemExpanded) {
      this.collapseItemWithActivation();
    } else {
      this.expandItem();
    }
  }

  // CASO 3: Toggle espansione + reset elementi fratelli e non-parenti
  private handleChildrenWithChildren() {
    const willExpand = !this.itemExpanded;

    // Singola iterazione per gestire fratelli e non-parenti
    SidebarNavItemStatusDirective.instances.forEach((instance) => {
      if (instance === this) return;

      const isSibling = this.areSiblings(this.item, instance.item);
      const isRelated = this.areRelated(this.item, instance.item);

      if (willExpand && isSibling) {
        instance.forceCollapseItem();
      } else if (!isRelated) {
        instance.collapseItem();
      }
    });

    // Toggle espansione dell'elemento corrente
    if (this.itemExpanded) {
      this.collapseItemWithActivation();
    } else {
      this.expandItem();
    }
  }

  // CASO 4: Reset stato attivo di tutti + attivazione elemento cliccato
  private handleChildrenWithoutChildren() {
    this.resetAllActiveStates();
    SidebarNavItemStatusDirective.instances.forEach((instance) => {
      if (instance !== this && !this.areRelated(this.item, instance.item)) {
        instance.collapseItem();
      }
    });
    this.setItemToActive();
  }

  private initialState() {
    const currentUrl = this.router.url;

    /**
     * Ricerca ricorsiva nell'albero
     * @param item NavItem corrente
     * @returns true se item o uno dei discendenti matcha la route
     */
    const updateStateRecursively = (item: NavItem): boolean => {
      const instance = SidebarNavItemStatusDirective.getInstance(item);

      // Caso elemento senza figli
      if (!item.children?.length) {
        if (item.route === currentUrl) {
          instance?.setItemToActive(); // diventa attivo subito
          return true;
        }
        return false;
      }

      // Caso nodo con figli
      let matchInChildren = false;

      for (const child of item.children) {
        if (updateStateRecursively(child)) {
          matchInChildren = true;
        }
      }

      // Se uno dei figli (o discendenti più profondi) matcha → espandi solo se non è stato chiuso manualmente
      if (instance && matchInChildren && !instance.manuallyCollapsed) {
        instance.expandItem();
      }

      return matchInChildren;
    };

    // Identifica gli elementi root in una singola iterazione
    const rootItems: NavItem[] = [];
    SidebarNavItemStatusDirective.instances.forEach((inst) => {
      const hasParent = SidebarNavItemStatusDirective.instances.some((i) =>
        i.item.children?.includes(inst.item)
      );
      if (!hasParent) {
        rootItems.push(inst.item);
      }
    });

    // Processa solo gli elementi root
    rootItems.forEach((rootItem) => updateStateRecursively(rootItem));
  }

  private setItemToActive() {
    this.el.nativeElement.classList.add('mat-accent');
    this.el.nativeElement.classList.add('activeMenu');
  }

  private setItemToInactive() {
    this.el.nativeElement.classList.remove('mat-accent');
    this.el.nativeElement.classList.remove('activeMenu');
  }

  private expandItem() {
    this.el.nativeElement.classList.add('expanded');
    this.itemExpanded = true;
    this.manuallyCollapsed = false; // Reset del flag quando espanso
    this.isExpandedEmitter.emit(true);

    // Se questo elemento contiene la route corrente, attiva il discendente più vicino
    if (this.isDescendantOf(this.item, this.router.url)) {
      this.resetAllActiveStates();
      this.activateNearestDescendant();
    }
  }

  private collapseItem(
    options: {
      forceCollapse?: boolean;
      activateIfContainsRoute?: boolean;
    } = {}
  ) {
    this.el.nativeElement.classList.remove('expanded');
    this.itemExpanded = false;
    this.isExpandedEmitter.emit(false);

    if (options.forceCollapse) {
      this.manuallyCollapsed = true;
    }

    if (
      options.activateIfContainsRoute &&
      this.isDescendantOf(this.item, this.router.url)
    ) {
      this.resetAllActiveStates();
      this.setItemToActive();
    }
  }

  private forceCollapseItem() {
    this.collapseItem({ forceCollapse: true, activateIfContainsRoute: true });
  }

  private collapseItemWithActivation() {
    this.collapseItem({ activateIfContainsRoute: true });
  }

  // Metodo comune per resettare tutti gli stati attivi
  private resetAllActiveStates() {
    SidebarNavItemStatusDirective.instances.forEach((instance) => {
      instance.setItemToInactive();
    });
  }

  // Attiva l'elemento più profondo visibile che contiene la route corrente
  private activateNearestDescendant() {
    const currentUrl = this.router.url;

    const findDeepestVisible = (item: NavItem): NavItem | null => {
      if (item.route === currentUrl) {
        return item;
      }

      if (item.children) {
        const instance = SidebarNavItemStatusDirective.getInstance(item);

        // Se l'elemento è espanso, cerca nei figli
        if (instance?.itemExpanded) {
          for (const child of item.children) {
            const result = findDeepestVisible(child);
            if (result) return result;
          }
        }

        // Se non è espanso ma contiene la route, ritorna questo elemento
        if (this.isDescendantOf(item, currentUrl)) {
          return item;
        }
      }

      return null;
    };

    if (this.item.children) {
      for (const child of this.item.children) {
        const targetItem = findDeepestVisible(child);
        if (targetItem) {
          const instance =
            SidebarNavItemStatusDirective.getInstance(targetItem);
          instance?.setItemToActive();
          break;
        }
      }
    }
  }

  // Helper: verifica se childRoute è discendente di parent
  private isDescendantOf(parent: NavItem, childRoute: string): boolean {
    if (!parent.children) return false;

    for (const child of parent.children) {
      if (child.route === childRoute) return true;
      if (this.isDescendantOf(child, childRoute)) return true;
    }

    return false;
  }

  // Helper: verifica se due elementi sono parenti (uno è antenato dell'altro)
  private areRelated(item1: NavItem, item2: NavItem): boolean {
    return (
      this.isDescendantOf(item1, item2.route!) ||
      this.isDescendantOf(item2, item1.route!)
    );
  }

  // Helper: verifica se l'elemento corrente è un discendente di qualche altro elemento
  private isDescendant(): boolean {
    return SidebarNavItemStatusDirective.instances.some(
      (inst) =>
        inst !== this && this.isDescendantOf(inst.item, this.item.route!)
    );
  }

  // Helper: verifica se due elementi sono fratelli (hanno lo stesso genitore)
  private areSiblings(item1: NavItem, item2: NavItem): boolean {
    const parent1 = this.findParent(item1);
    const parent2 = this.findParent(item2);
    return parent1 === parent2; // Anche gli elementi root (parent=null) sono fratelli tra loro
  }

  // Helper: trova il genitore di un elemento
  private findParent(targetItem: NavItem): NavItem | null {
    for (const instance of SidebarNavItemStatusDirective.instances) {
      if (instance.item.children?.includes(targetItem)) {
        return instance.item;
      }
    }
    return null; // È un elemento root
  }

  ngOnDestroy(): void {
    SidebarNavItemStatusDirective._instances.delete(this.item);
  }
}
